import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import {
  exportScenarioMp4Stream,
  exportScenariosMp4Concat,
} from '../../../features/scenarios/api';
import { useToast } from '../../../hooks/useToast';
import type { Scenario } from '../scenario/types';
import { waitForNextPoll } from '../../../utils/polling';
import { previewFilename, concatPreviewFilename } from '../../../utils/filename';
import { notifyJobComplete } from '../../../utils/jobNotifications';
import { useRecommendations, type RecommendationSubject } from '../hooks/useRecommendations';
import { useRecommendationEvents } from '../hooks/useRecommendationEvents';
import { extractAsin } from '../../../features/products/asinInput';

/** URLにexpress_jobパラメータを設定/削除する */
function setExpressJobParam(jobId: string | null) {
  const url = new URL(window.location.href);
  if (jobId) {
    url.searchParams.set('express_job', jobId);
  } else {
    url.searchParams.delete('express_job');
  }
  window.history.replaceState({}, '', url.toString());
}

function getExpressJobParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('express_job');
}

/** Wake Lockを取得・解放するヘルパー。エクスポート中のスリープを防止する。 */
async function requestWakeLock(): Promise<WakeLockSentinel | null> {
  try {
    if ('wakeLock' in navigator) {
      return await navigator.wakeLock.request('screen');
    }
  } catch {
    // Wake Lock取得失敗（バッテリー低下等）は無視して処理を続行
  }
  return null;
}

const SELECTED_ACCOUNT_KEY = 'selectedAmazonAccountId';
const ASPECT_RATIO_KEY = 'expressAspectRatio';
const SCENARIO_COUNT_KEY = 'expressScenarioCount';
const DURATION_KEY = 'expressDuration';

// 想定尺の選択肢 (秒)。Seedance 2.0 Mini は 4..15 秒のみ生成可能なため 4〜15 秒を 1 秒刻みで。
// 値は素の秒数文字列 (シナリオ生成の duration ヒント / Seedance 出力尺の双方で digit 抽出される)。
export const EXPRESS_DURATION_OPTIONS: Array<{ value: string; label: string }> =
  Array.from({ length: 12 }, (_, i) => {
    const sec = i + 4;
    return { value: String(sec), label: `${sec}秒` };
  });
const DEFAULT_DURATION = '15';
const PENDING_EXPORT_KEY = 'expressPendingExport';

interface PendingExport {
  selectedScenarioIds: string[];
  exportType: 'zip' | 'concat';
  identifier: string;
}

function savePendingExport(data: PendingExport) {
  sessionStorage.setItem(PENDING_EXPORT_KEY, JSON.stringify(data));
}

function loadPendingExport(): PendingExport | null {
  try {
    const raw = sessionStorage.getItem(PENDING_EXPORT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearPendingExport() {
  sessionStorage.removeItem(PENDING_EXPORT_KEY);
}

/** ストーリーボードジョブの per-scenario 進捗 (backend result_data.scenarios_progress) */
interface ScenarioProgressRaw {
  storyboard_status: string;
  storyboard_image_url?: string | null;
  video_status: string;
  video_material_id?: string | null;
  duration_seconds?: number | null;
  error?: string | null;
}

/** Express ジョブのポーリングレスポンス (storyboard 方式) */
interface ExpressJobResultData {
  phase: string; // scenario_generation | updating_scenarios | storyboard | video | mp4_export | done | failed
  scenario_ids?: string[];
  /** scenario-only / full ジョブで生成されたシナリオ候補 */
  scenarios?: unknown[];
  /** storyboard/video ジョブの per-scenario 進捗 */
  scenarios_progress?: Record<string, ScenarioProgressRaw>;
  total?: number;
  storyboards_done?: number;
  videos_done?: number;
  videos_failed?: number;
  /** 連結MP4事前生成の状態 */
  mp4_status?: 'generating' | 'done' | 'failed';
  mp4_download_url?: string;
  mp4_error?: string;
  error?: string;
}

interface ExpressJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number | null;
  result_data: ExpressJobResultData | null;
  error_message: string | null;
}

export interface AmazonProductInfo {
  /** amazon_products.id (UUID)。商品AIプロファイル推奨 (subject_type='amazon_product') の
   *  subject_id に使う。SP-API 経路 (account_id 指定) は必ず含む。 */
  id?: string;
  asin: string;
  title: string;
  brand_name?: string;
  main_image_url?: string;
  price_amount?: number;
  price_currency?: string;
  description?: string;
  bullet_points?: string[];
  sku?: string;
}

export type ExpressStep = 1 | 2 | 3 | 4;

export type AssetStatus = 'pending' | 'generating' | 'done' | 'failed';

/** 1 シナリオ案の素材生成状態 (絵コンテ → Seedance 動画)。 */
export interface ScenarioAssetStatus {
  storyboardStatus: AssetStatus;
  storyboardImageUrl?: string;
  videoStatus: AssetStatus;
  videoMaterialId?: string;
  durationSeconds?: number;
  error?: string;
}

/** 特急モードのキャスティング = Seedance preset actor。最大 2 人選択でき、
 *  選択数が登場人物数 (0=無指定 / 1 / 2) を決める。2 人時はセリフ=会話。 */
export interface ExpressSeedanceActor {
  id: string;
  byteplus_asset_id: string;
  display_name: string;
  thumbnail_public_url?: string;
  gender?: string;
  min_age?: number;
  max_age?: number;
  ethnicity?: string;
}

/** 同時に選べる登場人物の上限 (Seedance preset actor)。 */
export const EXPRESS_MAX_ACTORS = 2;

interface ExpressModeState {
  step: ExpressStep;
  maxStepReached: ExpressStep;
  // Step 1
  sourceType: 'asin' | 'manual';
  asin: string;
  accountId: string;
  manualProductId: string | null;
  manualProductTitle: string | null;
  manualProductImageUrl: string | null;
  /** amazon_products.id (UUID)。商品AIプロファイル推奨の subject_id 導出用
   *  (計画書§5.4)。ASIN検索登録 / 登録済みAmazon商品選択のいずれでも確定時にセットする。 */
  amazonProductId: string | null;
  productInfo: AmazonProductInfo | null;
  isSearching: boolean;
  isRegistering: boolean;
  registered: boolean;
  searchError: string | null;
  // Step 2
  tone: string;
  /** ユーザーがトーンを明示操作 (選択 or ✕クリア) したか。false のうちは推奨既定
   *  (recommendedTone) を「おすすめ」として表示・送信対象にできる (計画書§5.4)。 */
  toneTouched: boolean;
  aspectRatio: '16:9' | '9:16';
  scenarioCount: number;
  /** 想定尺 (秒・素数値文字列)。シナリオ生成ヒント兼 Seedance 出力尺。 */
  duration: string;
  /** 選択中の Seedance preset actor (最大 2)。選択数が登場人物数を決める。 */
  selectedPresetActors: ExpressSeedanceActor[];
  // Step 3 絵コンテ: シナリオ生成ローディング → 絵コンテ生成 → 選択
  isGenerating: boolean;
  scenarios: Scenario[];
  selectedScenarioIds: string[];
  // Step 3 (素材自動生成: 絵コンテ + Seedance 動画)
  isAutoGenerating: boolean;
  autoGeneratePhase: 'storyboard' | 'video' | 'mp4_export' | 'done' | null;
  /** バックグラウンドジョブID（ジョブベース自動生成時） */
  autoGenerateJobId: string | null;
  /** 各シナリオ案の素材状態 */
  scenarioAssetStatus: Record<string, ScenarioAssetStatus>;
  assetCounts: { storyboardsDone: number; videosDone: number; videosFailed: number; total: number };
  /** 絵コンテ生成が完了し Step 3 で選択できる状態か */
  storyboardsReady: boolean;
  /** 動画生成が完了し Step 4 (ダウンロード) へ進める状態か */
  assetsReady: boolean;
  // Step 4 (動画 / MP4エクスポート)
  isExporting: boolean;
  exportComplete: false | 'zip' | 'concat';
  exportStatus: string;
  /** スリープ復帰時の再ダウンロード用 */
  pendingRedownload: PendingExport | null;
  /** バックエンド事前生成MP4のダウンロードURL */
  mp4DownloadUrl: string | null;
  /** バックエンドでMP4事前生成中かどうか */
  mp4Generating: boolean;
}

const initialState: ExpressModeState = {
  step: 1,
  maxStepReached: 1,
  sourceType: 'asin',
  asin: '',
  accountId: localStorage.getItem(SELECTED_ACCOUNT_KEY) || '',
  manualProductId: null,
  manualProductTitle: null,
  manualProductImageUrl: null,
  amazonProductId: null,
  productInfo: null,
  isSearching: false,
  isRegistering: false,
  registered: false,
  searchError: null,
  tone: '',
  toneTouched: false,
  aspectRatio: (localStorage.getItem(ASPECT_RATIO_KEY) as '16:9' | '9:16') || '16:9',
  scenarioCount: Math.max(1, Math.min(10, Number(localStorage.getItem(SCENARIO_COUNT_KEY)) || 3)),
  duration: EXPRESS_DURATION_OPTIONS.some(o => o.value === localStorage.getItem(DURATION_KEY))
    ? (localStorage.getItem(DURATION_KEY) as string)
    : DEFAULT_DURATION,
  selectedPresetActors: [],
  isGenerating: false,
  scenarios: [],
  selectedScenarioIds: [],
  isAutoGenerating: false,
  autoGeneratePhase: null,
  autoGenerateJobId: null,
  scenarioAssetStatus: {},
  assetCounts: { storyboardsDone: 0, videosDone: 0, videosFailed: 0, total: 0 },
  storyboardsReady: false,
  assetsReady: false,
  isExporting: false,
  exportComplete: false,
  exportStatus: '',
  pendingRedownload: null,
  mp4DownloadUrl: null,
  mp4Generating: false,
};

function _normStatus(s: string | undefined): AssetStatus {
  if (s === 'generating' || s === 'done' || s === 'failed') return s;
  return 'pending';
}

/** backend の result_data から Step 3 用の部分 state を導出する (進捗マージ)。 */
function reduceStoryboardProgress(
  rd: ExpressJobResultData,
  prev: ExpressModeState,
): Partial<ExpressModeState> {
  const sp = rd.scenarios_progress || {};
  const scenarioAssetStatus: Record<string, ScenarioAssetStatus> = { ...prev.scenarioAssetStatus };
  for (const [sid, info] of Object.entries(sp)) {
    scenarioAssetStatus[sid] = {
      storyboardStatus: _normStatus(info.storyboard_status),
      storyboardImageUrl: info.storyboard_image_url ?? prev.scenarioAssetStatus[sid]?.storyboardImageUrl ?? undefined,
      videoStatus: _normStatus(info.video_status),
      videoMaterialId: info.video_material_id ?? undefined,
      durationSeconds: info.duration_seconds ?? undefined,
      error: info.error ?? undefined,
    };
  }
  const phaseMap: Record<string, ExpressModeState['autoGeneratePhase']> = {
    storyboard: 'storyboard',
    video: 'video',
    mp4_export: 'mp4_export',
    done: 'done',
  };
  const phase = phaseMap[rd.phase] ?? prev.autoGeneratePhase ?? 'storyboard';
  const assetsReady = rd.phase === 'mp4_export' || rd.phase === 'done';
  return {
    scenarioAssetStatus,
    autoGeneratePhase: phase,
    assetCounts: {
      storyboardsDone: rd.storyboards_done || 0,
      videosDone: rd.videos_done || 0,
      videosFailed: rd.videos_failed || 0,
      total: rd.total || prev.assetCounts.total || 0,
    },
    ...(assetsReady ? { assetsReady: true, maxStepReached: 4 as ExpressStep } : {}),
    ...(rd.phase === 'mp4_export' ? { mp4Generating: true } : {}),
    ...(rd.mp4_download_url ? { mp4DownloadUrl: rd.mp4_download_url, mp4Generating: false } : {}),
  };
}

/** シナリオ生成ペイロードを組み立てる。model='random' (モデル選定ランダム)。
 *  登場人物数 = 選択 Seedance preset actor 数 (0=無指定 / 1 / 2)。2 人時はセリフ=会話。 */
function buildScenarioPayload(s: {
  sourceType: 'asin' | 'manual';
  asin: string;
  productInfo: AmazonProductInfo | null;
  manualProductId: string | null;
  aspectRatio: string;
  scenarioCount: number;
  tone: string;
  duration: string;
  selectedPresetActors: ExpressSeedanceActor[];
}): Record<string, unknown> {
  const actors = s.selectedPresetActors;
  const count = actors.length;
  const base: Record<string, unknown> = {
    count: s.scenarioCount,
    model: 'random',
    assets: [],
    aspect_ratio: s.aspectRatio,
    tone: s.tone || undefined,
    duration: s.duration || DEFAULT_DURATION,
    // 特急モードは絵コンテ→Seedance のストーリーボード方式。これを宣言しないとシナリオ生成が
    // 紙芝居前提 (各シーン4〜6秒固定) のプロンプトになり、想定尺ベースのシーン尺バジェット指示と
    // storyboard_duration_seconds 算出が行われず、想定尺が AI に正しく伝わらない。
    video_generation_mode: 'storyboard',
    include_telop: true,
    telop_generate_only: true,
    // 2 人時はセリフ=会話。属性ツリーの dialogue='conversation' を送り、掛け合い指示
    // (一人語り禁止) を注入する。0〜1 人はセリフなし(ナレ主体)で dialogue キーは送らず
    // include_dialogue=false を維持する (tree が legacy bool を上書きしないようにする)。
    include_dialogue: count === 2,
    ...(count === 2 ? { dialogue: 'conversation' } : {}),
  };
  if (count > 0) {
    // character_count は属性ツリーの option key。tree 無効時は seedance preset の人数が
    // 性別構成プロンプト経由で人数を決めるためフォールバックとして機能する。
    base.character_count = count === 2 ? 'cc_2' : 'cc_1';
    base.seedance_preset_actor_ids = actors.map(a => a.id);
    base.seedance_preset_actors = actors.map(a => ({
      id: a.id,
      byteplus_asset_id: a.byteplus_asset_id,
      display_name: a.display_name,
      gender: a.gender,
      min_age: a.min_age,
      max_age: a.max_age,
      ethnicity: a.ethnicity,
    }));
  }
  if (s.sourceType === 'manual') {
    return { ...base, source_type: 'manual', product_id: s.manualProductId };
  }
  const resolvedAsin = s.productInfo?.asin || extractAsin(s.asin).asin;
  const asinPayload: Record<string, unknown> = { ...base, source_type: 'asin', asin: resolvedAsin };
  if (s.productInfo) {
    asinPayload.product_info = {
      title: s.productInfo.title,
      brand_name: s.productInfo.brand_name,
      description: s.productInfo.description,
      bullet_points: s.productInfo.bullet_points,
    };
  }
  return asinPayload;
}

interface UseExpressModeOptions {
  initialAsin?: string;
  onInitialAsinConsumed?: () => void;
  /** モーダルが開いているときだけ外部依存のデータを取得する。未指定時は従来通り常時取得 */
  enabled?: boolean;
}

export function useExpressMode(options: UseExpressModeOptions = {}) {
  const { initialAsin, onInitialAsinConsumed, enabled = true } = options;
  const [state, setState] = useState<ExpressModeState>(() => {
    // ページ復帰時に保存済みのエクスポート情報があれば、Step5で再ダウンロードUIを表示
    const pending = loadPendingExport();
    if (pending) {
      clearPendingExport();
      // モーダルを自動で開くイベントを次のティックで発火
      setTimeout(() => window.dispatchEvent(new Event('openExpressMode')), 0);
      return {
        ...initialState,
        step: 4 as ExpressStep,
        maxStepReached: 4 as ExpressStep,
        selectedScenarioIds: pending.selectedScenarioIds,
        pendingRedownload: pending,
      };
    }
    return initialState;
  });
  const toast = useToast();

  // 商品AIプロファイル推奨 (計画書§5.4)。Step1 で確定した商品を subject として推奨トーンを
  // 取得する。manual 商品は products.id、Amazon 商品は amazon_products.id (amazonProductId)。
  // どちらも未確定 (Step1未完了) の間は null → useRecommendations は enabled:false で待機する。
  const recommendationSubject = useMemo<RecommendationSubject | null>(() => {
    if (state.sourceType === 'manual' && state.manualProductId) {
      return { subject_type: 'product', subject_id: state.manualProductId };
    }
    if (state.sourceType === 'asin' && state.amazonProductId) {
      return { subject_type: 'amazon_product', subject_id: state.amazonProductId };
    }
    return null;
  }, [state.sourceType, state.manualProductId, state.amazonProductId]);
  const recommendationsQuery = useRecommendations(recommendationSubject);
  const recommendedTone = useMemo(() => {
    const data = recommendationsQuery.data;
    return data?.status === 'ready' ? (data.tone ?? null) : null;
  }, [recommendationsQuery.data]);
  // 送信・表示に使う実効トーン。ユーザーが未操作 (toneTouched=false) かつ推奨があれば
  // それを既定値として採用し、ユーザーが選択/クリア済みなら常に state.tone を優先する。
  const effectiveTone = useMemo(() => {
    if (state.toneTouched) return state.tone;
    return recommendedTone?.value || state.tone;
  }, [state.toneTouched, state.tone, recommendedTone]);

  // 特急トーン推奨の採用/解除ログ (計画書§8 P4)。生成実行 (startGeneration /
  // startFullGeneration) の直前に1回だけ記録する。推奨が無ければ何もしない。
  const { logRecommendationEvent } = useRecommendationEvents();
  const logExpressToneEvent = useCallback(() => {
    if (!recommendedTone) return;
    logRecommendationEvent({
      subject_type: recommendationSubject?.subject_type ?? null,
      subject_id: recommendationSubject?.subject_id ?? null,
      surface: 'express_tone',
      // 未操作 (toneTouched=false) なら推奨をそのまま採用、操作済みなら解除 (別の値を
      // 選んだ/クリアした) として扱う。
      action: state.toneTouched ? 'cleared' : 'adopted',
      group_key: 'tone',
      option_key: recommendedTone.value,
      profile_status: recommendationsQuery.data?.status ?? null,
    });
  }, [recommendedTone, recommendationSubject, state.toneTouched, recommendationsQuery.data, logRecommendationEvent]);

  // 別タブ・最小化中に生成が完了したことをブラウザ通知で知らせる。
  // フォアグラウンドでは notifyJobComplete 側で抑止され、トーストのみになる。
  const notifyStoryboardsDone = useCallback((jobId: string | null) => {
    notifyJobComplete({ type: 'storyboard', jobId: `${jobId ?? 'express'}:storyboard` });
  }, []);
  const notifyVideosDone = useCallback((jobId: string | null) => {
    notifyJobComplete({ type: 'video', jobId: `${jobId ?? 'express'}:video` });
  }, []);

  // Fetch Amazon accounts
  const { data: amazonAccounts } = useQuery({
    queryKey: ['amazon-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<Array<{ id: string; seller_id: string; marketplace_id: string; status: string }>>('/amazon/accounts');
      return data;
    },
    enabled,
  });

  const setAsin = useCallback((asin: string) => {
    setState(prev => ({ ...prev, asin, productInfo: null, registered: false, searchError: null }));
  }, []);

  const setAccountId = useCallback((accountId: string) => {
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
    setState(prev => ({ ...prev, accountId }));
  }, []);

  // 登録済み商品リストからの選択。Amazon 商品 (amazon_products) は asin 経路で扱う必要がある
  // (product_id を manual 経路に流すと products テーブルで解決できず商品画像/ロゴが添付されない)。
  const selectManualProduct = useCallback((
    productId: string,
    title: string,
    imageUrl?: string,
    amazon?: { asin: string },
  ) => {
    setState(prev => ({
      ...prev,
      sourceType: amazon ? ('asin' as const) : ('manual' as const),
      // Amazon 商品は asin 起点で解決するため product_id は持たない。表示用に title/image は保持。
      manualProductId: amazon ? null : productId,
      // 登録済み商品一覧の Amazon 商品は productId が amazon_products.id そのもの。
      amazonProductId: amazon ? productId : null,
      manualProductTitle: title,
      manualProductImageUrl: imageUrl || null,
      asin: amazon ? amazon.asin : '',
      productInfo: null,
      registered: true,
      step: 2,
      maxStepReached: Math.max(prev.maxStepReached, 2) as ExpressStep,
    }));
  }, []);

  // トーンの明示操作 (選択 / ✕クリアいずれも '' が来る)。呼ばれた時点で touched=true にし、
  // 以後は推奨既定 (recommendedTone) を自動適用しない (計画書§5.4)。
  const setTone = useCallback((tone: string) => {
    setState(prev => ({ ...prev, tone, toneTouched: true }));
  }, []);

  const setAspectRatio = useCallback((aspectRatio: '16:9' | '9:16') => {
    localStorage.setItem(ASPECT_RATIO_KEY, aspectRatio);
    setState(prev => ({ ...prev, aspectRatio }));
  }, []);

  const setScenarioCount = useCallback((scenarioCount: number) => {
    const clamped = Math.max(1, Math.min(10, scenarioCount));
    localStorage.setItem(SCENARIO_COUNT_KEY, String(clamped));
    setState(prev => ({ ...prev, scenarioCount: clamped }));
  }, []);

  const setDuration = useCallback((duration: string) => {
    localStorage.setItem(DURATION_KEY, duration);
    setState(prev => ({ ...prev, duration }));
  }, []);

  const setSelectedPresetActors = useCallback((actors: ExpressSeedanceActor[]) => {
    // 上限 (EXPRESS_MAX_ACTORS) を超えないよう防御的にクランプ。
    setState(prev => ({ ...prev, selectedPresetActors: actors.slice(0, EXPRESS_MAX_ACTORS) }));
  }, []);

  // Step 1: Search ASIN
  const searchAsin = useCallback(async () => {
    if (!state.asin.trim()) return;
    const extracted = extractAsin(state.asin);
    setState(prev => ({ ...prev, isSearching: true, searchError: null, productInfo: null }));

    try {
      let asin = extracted.asin;

      // 短縮URLの場合はサーバーサイドでリダイレクト解決
      if (extracted.needsResolve && extracted.originalUrl) {
        const { data: resolveData } = await apiClient.get(`/amazon/resolve-url?url=${encodeURIComponent(extracted.originalUrl)}`);
        asin = resolveData.asin;
      }

      if (!asin) {
        setState(prev => ({ ...prev, isSearching: false, searchError: 'ASINを抽出できませんでした' }));
        toast.error('ASINを抽出できませんでした');
        return;
      }

      // Update the displayed value to the extracted ASIN
      setState(prev => ({ ...prev, asin }));

      // ASIN検索はSP-API経由のみ (スクレイピング経路は2026-08-13に廃止)。
      // Amazon Sellerアカウント未連携の組織では利用できない。
      if (!state.accountId) {
        const message = 'ASIN検索にはAmazonアカウントの連携が必要です。設定画面からAmazon連携を行ってください。';
        setState(prev => ({ ...prev, isSearching: false, searchError: message }));
        toast.error(message);
        return;
      }

      const { data } = await apiClient.get(`/amazon/products/${asin}?account_id=${state.accountId}`);
      const product = data.product as AmazonProductInfo;

      setState(prev => ({ ...prev, productInfo: product, isSearching: false }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '商品情報の取得に失敗しました';
      setState(prev => ({ ...prev, isSearching: false, searchError: message }));
      toast.error('商品情報の取得に失敗しました');
    }
  }, [state.asin, state.accountId, toast]);

  // URLパラメータで渡されたASIN/Amazon URLをセットして自動検索
  const initialAsinProcessed = useRef(false);
  useEffect(() => {
    if (initialAsin && !initialAsinProcessed.current) {
      initialAsinProcessed.current = true;
      setState(prev => ({ ...prev, asin: initialAsin, productInfo: null, registered: false, searchError: null }));
      onInitialAsinConsumed?.();
    }
  }, [initialAsin, onInitialAsinConsumed]);

  // initialAsinがセットされたら自動検索を実行
  const autoSearchTriggered = useRef(false);
  useEffect(() => {
    if (initialAsinProcessed.current && !autoSearchTriggered.current && state.asin && !state.productInfo && !state.isSearching) {
      autoSearchTriggered.current = true;
      searchAsin();
    }
  }, [state.asin, state.productInfo, state.isSearching, searchAsin]);

  // Step 1: Register product
  const registerProduct = useCallback(() => {
    if (!state.productInfo) return;
    const amazonProductId = state.productInfo.id ?? null;
    setState(prev => ({ ...prev, sourceType: 'asin' as const, manualProductId: null, manualProductTitle: null, manualProductImageUrl: null, amazonProductId, registered: true, step: 2, maxStepReached: Math.max(prev.maxStepReached, 2) as ExpressStep }));
    toast.success('商品を登録しました');
  }, [state.productInfo, toast]);

  /** ジョブを 3 秒間隔でポーリングする共通ループ。ネットワークエラーは 10 連続まで許容。
   *  shouldStop() が true を返したら、それ以上ハンドラを呼ばず静かに終了する
   *  (絵コンテ生成中に動画生成へ進んだ場合に、絵コンテジョブが選択状態を上書きしないため)。 */
  const pollJob = useCallback(async (
    jobId: string,
    handlers: {
      onUpdate?: (rd: ExpressJobResultData) => void;
      onCompleted: (rd: ExpressJobResultData | null) => void;
      onFailed: (message: string) => void;
      maxPolls?: number;
      shouldStop?: () => boolean;
    },
  ) => {
    const POLL_INTERVAL = 3000;
    const MAX_POLLS = handlers.maxPolls ?? 400;
    let networkErrors = 0;

    for (let i = 0; i < MAX_POLLS; i++) {
      await waitForNextPoll(POLL_INTERVAL);
      if (handlers.shouldStop?.()) return;

      let statusData: ExpressJobStatus;
      try {
        const { data } = await apiClient.get<ExpressJobStatus>(
          `/generation/express/auto-generate-status/${jobId}`,
        );
        statusData = data;
        networkErrors = 0;
      } catch {
        networkErrors++;
        if (networkErrors >= 10) { handlers.onFailed('サーバーとの通信に失敗しました'); return; }
        continue;
      }

      if (handlers.shouldStop?.()) return;
      if (statusData.result_data && handlers.onUpdate) {
        handlers.onUpdate(statusData.result_data);
      }
      if (statusData.status === 'completed') { handlers.onCompleted(statusData.result_data); return; }
      if (statusData.status === 'failed') { handlers.onFailed(statusData.error_message || '生成に失敗しました'); return; }
    }
    handlers.onFailed('生成がタイムアウトしました');
  }, []);

  // 絵コンテ生成ポーリングを動画生成開始時に止めるためのフラグ。
  const cancelStoryboardPollRef = useRef(false);

  // Step 3: 全案の絵コンテのみ生成 (動画は作らない)。完了後ユーザーが絵コンテを選択する。
  const startStoryboardGeneration = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    cancelStoryboardPollRef.current = false;
    setState(prev => ({
      ...prev,
      step: 3,
      maxStepReached: Math.max(prev.maxStepReached, 3) as ExpressStep,
      isAutoGenerating: true,
      autoGeneratePhase: 'storyboard',
      storyboardsReady: false,
      assetsReady: false,
      autoGenerateJobId: null,
      // 既定は未選択。リセットは生成開始時のみ行い、完了時 (onCompleted) では維持する。
      selectedScenarioIds: [],
      scenarioAssetStatus: Object.fromEntries(ids.map(id => [id, { storyboardStatus: 'pending' as AssetStatus, videoStatus: 'pending' as AssetStatus }])),
      assetCounts: { storyboardsDone: 0, videosDone: 0, videosFailed: 0, total: ids.length },
    }));
    try {
      const { data: jobData } = await apiClient.post<{ job_id: string }>(
        '/generation/express/storyboard-generate-async',
        { scenario_ids: ids },
      );
      const jobId = jobData.job_id;
      setState(prev => ({ ...prev, autoGenerateJobId: jobId }));
      setExpressJobParam(jobId);
      await pollJob(jobId, {
        maxPolls: 300,
        shouldStop: () => cancelStoryboardPollRef.current,
        onUpdate: (rd) => setState(prev => ({ ...prev, ...reduceStoryboardProgress(rd, prev) })),
        onCompleted: (rd) => {
          setState(prev => {
            const merged = rd ? reduceStoryboardProgress(rd, prev) : {};
            return {
              ...prev,
              ...merged,
              isAutoGenerating: false,
              autoGeneratePhase: 'storyboard',
              autoGenerateJobId: null,
              storyboardsReady: true,
              // 選択は ...prev で維持する (既定は生成開始時に [] 済み)。完了時にリセットすると
              // 生成中にユーザーが選んだカードの選択が消えてしまうため、ここでは触らない。
            };
          });
          // express_job パラメータは敢えて残す。絵コンテ選択中(Step 3)に Mac スリープで
          // タブが破棄・リロードされても、resume 効果が完了済みジョブ(phase=storyboard_done)を
          // 再取得して選択画面を復元できるようにするため。動画生成開始時に上書き / 閉じる時に reset で消える。
          notifyStoryboardsDone(jobId);
        },
        onFailed: (message) => {
          setState(prev => ({ ...prev, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null }));
          setExpressJobParam(null);
          toast.error(message);
        },
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '絵コンテ生成に失敗しました';
      setState(prev => ({ ...prev, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null }));
      setExpressJobParam(null);
      toast.error(message);
    }
  }, [pollJob, toast, notifyStoryboardsDone]);

  // 任意フェーズの express ジョブをポーリングして UI に反映する共通処理。
  // リロード復帰 (resume effect) と、動画生成開始に失敗したときの絵コンテポーリング再開で共有する。
  const resumeJobPolling = useCallback((jobId: string) => {
    pollJob(jobId, {
      // 絵コンテ生成中に動画生成へ進んだら停止 (古い poll が選択状態を上書きしないように)。
      shouldStop: () => cancelStoryboardPollRef.current,
      onUpdate: (rd) => {
        const isScenarioPhase = rd.phase === 'scenario_generation' || rd.phase === 'updating_scenarios';
        if (isScenarioPhase) return;
        // 絵コンテ生成中は Step 3、動画/連結中は Step 4 で進捗を表示する。
        const isVideoPhase = rd.phase === 'video' || rd.phase === 'mp4_export';
        setState(prev => {
          const scenarios = (rd.scenarios && prev.scenarios.length === 0)
            ? (rd.scenarios as unknown as Scenario[])
            : prev.scenarios;
          const merged = rd.scenarios_progress ? reduceStoryboardProgress(rd, prev) : {};
          return {
            ...prev,
            ...merged,
            scenarios,
            isGenerating: false,
            isAutoGenerating: true,
            ...(isVideoPhase
              ? { step: 4 as ExpressStep, maxStepReached: 4 as ExpressStep, mp4Generating: true }
              : { step: 3 as ExpressStep, maxStepReached: Math.max(prev.maxStepReached, 3) as ExpressStep }),
          };
        });
      },
      onCompleted: (rd) => {
        // 絵コンテのみジョブ完了 → Step 3 (絵コンテ) で選択させる。
        // この場合は express_job パラメータを残し、再スリープ時も選択画面を復元できるようにする
        // (動画/シナリオ完了など他の分岐では下でクリアする)。
        if (rd?.phase === 'storyboard_done') {
          setState(prev => {
            const merged = reduceStoryboardProgress(rd, prev);
            const scenarios = prev.scenarios.length > 0 ? prev.scenarios : ((rd.scenarios as unknown as Scenario[]) || prev.scenarios);
            return {
              ...prev, ...merged, scenarios,
              isGenerating: false, isAutoGenerating: false,
              autoGeneratePhase: 'storyboard', autoGenerateJobId: null,
              // 選択は ...prev で維持 (完了時にリセットすると生成中の選択が消えるため触らない)。
              storyboardsReady: true,
              step: 3 as ExpressStep, maxStepReached: Math.max(prev.maxStepReached, 3) as ExpressStep,
            };
          });
          notifyStoryboardsDone(jobId);
          return;
        }
        setExpressJobParam(null);
        const hasAssets = !!(rd && ((rd.total && rd.total > 0) || (rd.scenarios_progress && Object.keys(rd.scenarios_progress).length > 0)));
        if (hasAssets) {
          setState(prev => {
            const merged = rd ? reduceStoryboardProgress(rd, prev) : {};
            const statusMap = merged.scenarioAssetStatus ?? prev.scenarioAssetStatus;
            const baseIds = prev.selectedScenarioIds.length > 0 ? prev.selectedScenarioIds : (rd?.scenario_ids ?? []);
            const successfulIds = baseIds.filter(id => statusMap[id]?.videoStatus === 'done');
            const scenarios = prev.scenarios.length > 0
              ? prev.scenarios
              : ((rd?.scenarios as unknown as Scenario[]) || prev.scenarios);
            return {
              ...prev, ...merged, scenarios,
              isGenerating: false, isAutoGenerating: false,
              autoGeneratePhase: 'done', autoGenerateJobId: null,
              step: 4 as ExpressStep, maxStepReached: 4 as ExpressStep,
              assetsReady: true,
              selectedScenarioIds: successfulIds.length > 0 ? successfulIds : baseIds,
              mp4DownloadUrl: rd?.mp4_download_url || prev.mp4DownloadUrl,
              mp4Generating: false,
            };
          });
          notifyVideosDone(jobId);
          if (rd?.mp4_download_url) toast.success('動画生成が完了しました');
        } else {
          // シナリオのみジョブ完了 → そのまま全案の絵コンテ生成へ。
          const scenarios = (rd?.scenarios as unknown as Scenario[]) || [];
          setState(prev => ({ ...prev, scenarios, isGenerating: false }));
          if (scenarios.length > 0) void startStoryboardGeneration(scenarios.map(s => s.id));
        }
      },
      onFailed: (message) => {
        setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGenerateJobId: null }));
        setExpressJobParam(null);
        toast.error(message);
      },
    });
  }, [pollJob, toast, startStoryboardGeneration, notifyStoryboardsDone, notifyVideosDone]);

  // Step 2 → Step 3 (絵コンテ): シナリオ生成と「完成ごとの絵コンテ生成」をパイプライン実行する
  // 1 つのジョブ。シナリオが 1 本できるたび絵コンテ生成が先行起動され、進捗が流れてくる。
  const startGeneration = useCallback(async () => {
    cancelStoryboardPollRef.current = false;
    setState(prev => ({ ...prev, step: 3, maxStepReached: Math.max(prev.maxStepReached, 3) as ExpressStep, isGenerating: true, isAutoGenerating: false, autoGeneratePhase: 'storyboard', scenarios: [], selectedScenarioIds: [], scenarioAssetStatus: {}, storyboardsReady: false, assetsReady: false }));
    logExpressToneEvent();
    // 未操作時は推奨トーン (effectiveTone) を送信値として採用する (計画書§5.4)。
    const scenarioPayload = buildScenarioPayload({ ...state, tone: effectiveTone });
    try {
      const { data: jobData } = await apiClient.post<{ job_id: string }>(
        '/generation/express/scenario-generate-async',
        { scenario_payload: scenarioPayload, aspect_ratio: state.aspectRatio },
      );
      const jobId = jobData.job_id;
      setState(prev => ({ ...prev, autoGenerateJobId: jobId }));
      setExpressJobParam(jobId);
      await pollJob(jobId, {
        shouldStop: () => cancelStoryboardPollRef.current,
        onUpdate: (rd) => {
          // シナリオ生成中はローディング、絵コンテフェーズに入ったら進捗 (カード) を流す。
          const isScenarioPhase = rd.phase === 'scenario_generation' || rd.phase === 'updating_scenarios';
          if (isScenarioPhase) return;
          setState(prev => {
            const scenarios = (rd.scenarios && (rd.scenarios as unknown[]).length > 0)
              ? (rd.scenarios as unknown as Scenario[])
              : prev.scenarios;
            const merged = rd.scenarios_progress ? reduceStoryboardProgress(rd, prev) : {};
            return { ...prev, ...merged, scenarios, isGenerating: false, isAutoGenerating: true };
          });
        },
        onCompleted: (rd) => {
          const scenarios = (rd?.scenarios as unknown as Scenario[]) || [];
          if (scenarios.length === 0) {
            // シナリオ生成自体が失敗 → 設定へ戻す。ここでは param をクリアして良い。
            setExpressJobParam(null);
            setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGenerateJobId: null, step: 2 as ExpressStep, maxStepReached: 2 as ExpressStep }));
            toast.error('シナリオを生成できませんでした');
            return;
          }
          // 絵コンテ完成 → Step 3 で選択。express_job パラメータは敢えて残す (絵コンテ選択中に
          // リロード/スリープ復帰しても resume が storyboard_done ジョブを復元できるように)。
          // 動画生成開始時に上書き / 閉じる時に reset でクリアされる。
          setState(prev => {
            const merged = rd ? reduceStoryboardProgress(rd, prev) : {};
            return {
              ...prev, ...merged, scenarios,
              isGenerating: false, isAutoGenerating: false,
              autoGeneratePhase: 'storyboard', autoGenerateJobId: null,
              // 選択は ...prev で維持 (完了時にリセットすると生成中の選択が消えるため触らない)。
              storyboardsReady: true,
            };
          });
          notifyStoryboardsDone(jobId);
        },
        onFailed: (message) => {
          setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGenerateJobId: null, step: 2 as ExpressStep, maxStepReached: 2 as ExpressStep }));
          setExpressJobParam(null);
          toast.error(message);
        },
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'シナリオ生成に失敗しました';
      setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGenerateJobId: null, step: 2 as ExpressStep, maxStepReached: 2 as ExpressStep }));
      setExpressJobParam(null);
      toast.error(message);
    }
  }, [state, effectiveTone, pollJob, toast, notifyStoryboardsDone, logExpressToneEvent]);

  // Step 3: 絵コンテの選択トグル (動画化する案 = 選択された絵コンテ)
  const selectScenario = useCallback((scenarioId: string) => {
    setState(prev => {
      const ids = prev.selectedScenarioIds;
      const next = ids.includes(scenarioId)
        ? ids.filter(id => id !== scenarioId)
        : [...ids, scenarioId];
      return { ...prev, selectedScenarioIds: next };
    });
  }, []);

  // Step 3 → 4: 選択された絵コンテの案だけ Seedance 動画 + 連結 MP4 を生成
  const startVideoGeneration = useCallback(async () => {
    if (state.selectedScenarioIds.length === 0) { toast.error('絵コンテを選択してください'); return; }
    // 絵コンテ生成がまだ続いていても、進行中のポーリングが選択状態を上書きしないよう止める。
    // 進行中の絵コンテジョブ ID (全案完了済みなら null)。動画開始失敗時のポーリング再開に使う。
    const storyboardJobId = state.autoGenerateJobId;
    cancelStoryboardPollRef.current = true;
    // 動画生成に失敗したら、止めた絵コンテポーリングを再開して残りの完了を反映できるようにする。
    // 進行中の絵コンテジョブがあれば express_job パラメータもそちらに向け直す (再読込時の復帰用)。
    // 戻り値: 再開したか (= 呼び出し側が param をクリアすべきでないか)。
    const restoreStoryboardPolling = (): boolean => {
      cancelStoryboardPollRef.current = false;
      if (!storyboardJobId) return false;
      setExpressJobParam(storyboardJobId);
      resumeJobPolling(storyboardJobId);
      return true;
    };
    const ids = state.selectedScenarioIds;
    setState(prev => ({
      ...prev,
      step: 4,
      maxStepReached: Math.max(prev.maxStepReached, 4) as ExpressStep,
      isAutoGenerating: true,
      autoGeneratePhase: 'video',
      assetsReady: false,
      mp4Generating: true,
      autoGenerateJobId: null,
      scenarioAssetStatus: Object.fromEntries(
        Object.entries(prev.scenarioAssetStatus).map(([id, st]) =>
          [id, ids.includes(id) ? { ...st, videoStatus: 'pending' as AssetStatus } : st]),
      ),
      assetCounts: { ...prev.assetCounts, videosDone: 0, videosFailed: 0, total: ids.length },
    }));
    try {
      const { data: jobData } = await apiClient.post<{ job_id: string }>(
        '/generation/express/video-generate-async',
        { scenario_ids: ids, aspect_ratio: state.aspectRatio },
      );
      const jobId = jobData.job_id;
      setState(prev => ({ ...prev, autoGenerateJobId: jobId }));
      setExpressJobParam(jobId);
      await pollJob(jobId, {
        maxPolls: 400,
        onUpdate: (rd) => setState(prev => ({ ...prev, ...reduceStoryboardProgress(rd, prev) })),
        onCompleted: (rd) => {
          setState(prev => {
            const merged = rd ? reduceStoryboardProgress(rd, prev) : {};
            const statusMap = merged.scenarioAssetStatus ?? prev.scenarioAssetStatus;
            const successfulIds = ids.filter(id => statusMap[id]?.videoStatus === 'done');
            return {
              ...prev,
              ...merged,
              isAutoGenerating: false,
              autoGeneratePhase: 'done',
              autoGenerateJobId: null,
              assetsReady: true,
              maxStepReached: 4 as ExpressStep,
              selectedScenarioIds: successfulIds.length > 0 ? successfulIds : ids,
              mp4DownloadUrl: rd?.mp4_download_url || prev.mp4DownloadUrl,
              mp4Generating: false,
            };
          });
          setExpressJobParam(null);
          notifyVideosDone(jobId);
          const failed = rd?.videos_failed ?? 0;
          if (failed > 0) toast.error(`${failed}案の動画生成に失敗しました（成功分のみ出力します）`);
          else toast.success('動画生成が完了しました');
          if (rd?.mp4_status === 'failed') {
            toast.error('連結MP4の事前生成に失敗しました。動画タブから手動でエクスポートできます。');
          }
        },
        onFailed: (message) => {
          // 動画失敗時は絵コンテ選択 (Step 3) に戻して再試行できるようにする。
          // 動画ポーリングは既に終了しているので、止めた絵コンテポーリングをここで再開する
          // (絵コンテジョブが未完了なら残りの完了を反映、完了済みなら storyboard_done で選択画面を復元)。
          const restored = restoreStoryboardPolling();
          setState(prev => ({ ...prev, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null, mp4Generating: false, step: 3 as ExpressStep }));
          if (!restored) setExpressJobParam(null);
          toast.error(message);
        },
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '動画生成に失敗しました';
      // 動画ジョブ起動に失敗 → 止めた絵コンテポーリングを再開し、Step 3 に戻して再試行できるようにする。
      const restored = restoreStoryboardPolling();
      setState(prev => ({ ...prev, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null, mp4Generating: false, step: 3 as ExpressStep }));
      if (!restored) setExpressJobParam(null);
      toast.error(message);
    }
  }, [state.selectedScenarioIds, state.aspectRatio, state.autoGenerateJobId, pollJob, toast, notifyVideosDone, resumeJobPolling]);

  // Step 4: Export MP4 (全選択シナリオを順次ダウンロード)
  const getProductName = useCallback(() => {
    return state.productInfo?.title || state.manualProductTitle || 'preview';
  }, [state.productInfo?.title, state.manualProductTitle]);

  const getScenarioTitle = useCallback((scenarioId: string) => {
    return state.scenarios.find(s => s.id === scenarioId)?.title || 'preview';
  }, [state.scenarios]);

  const exportMp4Zip = useCallback(async () => {
    if (state.selectedScenarioIds.length === 0) return;
    setState(prev => ({ ...prev, isExporting: true, exportStatus: 'MP4を生成中...' }));
    const onProgress = (message: string) => {
      setState(prev => ({ ...prev, exportStatus: message }));
    };
    const identifier = state.asin || state.manualProductId || 'unknown';
    savePendingExport({ selectedScenarioIds: state.selectedScenarioIds, exportType: 'zip', identifier });
    const wakeLock = await requestWakeLock();
    try {
      if (state.selectedScenarioIds.length === 1) {
        // 単一シナリオ: 事前生成済みURLがあればそれを使用、なければ生成
        const pregenUrl = state.mp4DownloadUrl;
        const dlFilename = previewFilename(getScenarioTitle(state.selectedScenarioIds[0]));
        let blob: Blob;
        if (pregenUrl && !pregenUrl.startsWith('blob:')) {
          try {
            setState(prev => ({ ...prev, exportStatus: '事前生成済み動画をダウンロード中...' }));
            const resp = await fetch(pregenUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            blob = await resp.blob();
          } catch (fetchErr) {
            console.warn('Pre-generated MP4 fetch failed, falling back to regeneration:', fetchErr);
            setState(prev => ({ ...prev, exportStatus: 'MP4を生成中...' }));
            blob = await exportScenarioMp4Stream(state.selectedScenarioIds[0], { burn_telop: true }, onProgress);
          }
        } else if (pregenUrl?.startsWith('blob:')) {
          // 既にblob URLがある場合は即ダウンロード発火
          const a = document.createElement('a');
          a.href = pregenUrl;
          a.download = dlFilename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          clearPendingExport();
          setState(prev => ({ ...prev, isExporting: false, exportComplete: 'zip' }));
          wakeLock?.release();
          return;
        } else {
          blob = await exportScenarioMp4Stream(state.selectedScenarioIds[0], { burn_telop: true }, onProgress);
        }
        const blobUrl = URL.createObjectURL(blob);

        // ダウンロードを即発火
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = dlFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        clearPendingExport();
        setState(prev => ({ ...prev, isExporting: false, exportComplete: 'zip', exportStatus: '', pendingRedownload: null, mp4DownloadUrl: blobUrl }));
        toast.success('プレビュー動画のダウンロードを開始しました');
      } else {
        // 複数シナリオ: ZIPで一括生成してダウンロードリンクを表示
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        const total = state.selectedScenarioIds.length;

        for (let idx = 0; idx < total; idx++) {
          const scenarioId = state.selectedScenarioIds[idx];
          const scenarioIdx = state.scenarios.findIndex(s => s.id === scenarioId) + 1;
          const sceneProgress = (message: string) => {
            setState(prev => ({ ...prev, exportStatus: `案${scenarioIdx} (${idx + 1}/${total}): ${message}` }));
          };
          const blob = await exportScenarioMp4Stream(scenarioId, { burn_telop: true }, sceneProgress);
          const title = getScenarioTitle(scenarioId);
          zip.file(previewFilename(title), blob);
        }

        setState(prev => ({ ...prev, exportStatus: 'ZIP圧縮中...' }));
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const blobUrl = URL.createObjectURL(zipBlob);

        clearPendingExport();
        setState(prev => ({ ...prev, isExporting: false, exportComplete: 'zip', exportStatus: '', pendingRedownload: null, mp4DownloadUrl: blobUrl }));
        toast.success(`${total}件のプレビュー動画の生成が完了しました`);
      }
    } catch {
      setState(prev => ({ ...prev, isExporting: false, exportStatus: '' }));
      toast.error('ダウンロードに失敗しました');
    } finally {
      wakeLock?.release();
    }
  }, [state.selectedScenarioIds, state.scenarios, state.asin, state.manualProductId, state.mp4DownloadUrl, getScenarioTitle, toast]);

  // 全シナリオを連結した1本のMP4をダウンロード
  const exportMp4Concat = useCallback(async () => {
    if (state.selectedScenarioIds.length === 0) return;
    const identifier = state.asin || state.manualProductId || 'unknown';
    savePendingExport({ selectedScenarioIds: state.selectedScenarioIds, exportType: 'concat', identifier });
    setState(prev => ({ ...prev, isExporting: true }));
    const wakeLock = await requestWakeLock();
    const dlFilename = concatPreviewFilename(getProductName());
    try {
      let blob: Blob;

      // 事前生成済みのURLがある場合はそれをフェッチ（再生成を回避）
      const pregenUrl = state.mp4DownloadUrl;
      if (pregenUrl && !pregenUrl.startsWith('blob:')) {
        try {
          setState(prev => ({ ...prev, exportStatus: '事前生成済み動画をダウンロード中...' }));
          const resp = await fetch(pregenUrl);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          blob = await resp.blob();
        } catch (fetchErr) {
          // 署名URL期限切れなどの場合はフォールバックで再生成
          console.warn('Pre-generated MP4 fetch failed, falling back to regeneration:', fetchErr);
          setState(prev => ({ ...prev, exportStatus: '動画を生成しています...' }));
          blob = await exportScenariosMp4Concat(state.selectedScenarioIds, { burn_telop: true });
        }
      } else if (pregenUrl?.startsWith('blob:')) {
        // 既にblob URLがある場合は即ダウンロード発火
        const a = document.createElement('a');
        a.href = pregenUrl;
        a.download = dlFilename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        clearPendingExport();
        setState(prev => ({ ...prev, isExporting: false, exportComplete: 'concat' }));
        wakeLock?.release();
        return;
      } else {
        blob = await exportScenariosMp4Concat(state.selectedScenarioIds, { burn_telop: true });
      }

      const blobUrl = URL.createObjectURL(blob);

      // ダウンロードを即発火
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = dlFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      clearPendingExport();
      setState(prev => ({ ...prev, isExporting: false, exportStatus: '', exportComplete: 'concat', pendingRedownload: null, mp4DownloadUrl: blobUrl }));
      toast.success('連結プレビュー動画のダウンロードを開始しました');
    } catch {
      setState(prev => ({ ...prev, isExporting: false, exportStatus: '' }));
      toast.error('連結MP4の生成に失敗しました');
    } finally {
      wakeLock?.release();
    }
  }, [state.selectedScenarioIds, state.asin, state.manualProductId, state.mp4DownloadUrl, getProductName, toast]);

  // 一気に動画生成: シナリオ生成(random) → 全案の絵コンテ+Seedance動画 → 連結MP4
  const startFullGeneration = useCallback(async () => {
    setState(prev => ({ ...prev, step: 3, maxStepReached: Math.max(prev.maxStepReached, 3) as ExpressStep, isGenerating: true }));
    logExpressToneEvent();
    const scenarioPayload = buildScenarioPayload({ ...state, tone: effectiveTone });

    try {
      const { data: jobData } = await apiClient.post<{ job_id: string }>(
        '/generation/express/full-generate-async',
        { scenario_payload: scenarioPayload, aspect_ratio: state.aspectRatio },
      );
      const jobId = jobData.job_id;
      setState(prev => ({ ...prev, autoGenerateJobId: jobId }));
      setExpressJobParam(jobId);

      await pollJob(jobId, {
        onUpdate: (rd) => {
          const isScenarioPhase = rd.phase === 'scenario_generation' || rd.phase === 'updating_scenarios';
          // フルは選択を挟まないため、シナリオが揃ったら動画 (Step 4) で進捗表示する。
          if (rd.scenarios && !isScenarioPhase) {
            setState(prev => {
              if (prev.scenarios.length > 0) return prev;
              const scenariosList = rd.scenarios as unknown as Scenario[];
              return {
                ...prev,
                scenarios: scenariosList,
                selectedScenarioIds: scenariosList.map(s => s.id),
                isGenerating: false,
                step: 4 as ExpressStep,
                maxStepReached: 4 as ExpressStep,
                isAutoGenerating: true,
                mp4Generating: true,
                autoGeneratePhase: 'storyboard',
                assetCounts: { storyboardsDone: 0, videosDone: 0, videosFailed: 0, total: scenariosList.length },
              };
            });
          }
          if (!isScenarioPhase && rd.scenarios_progress) {
            setState(prev => ({ ...prev, ...reduceStoryboardProgress(rd, prev) }));
          }
        },
        onCompleted: (rd) => {
          setState(prev => {
            const merged = rd ? reduceStoryboardProgress(rd, prev) : {};
            const statusMap = merged.scenarioAssetStatus ?? prev.scenarioAssetStatus;
            const successfulIds = prev.selectedScenarioIds.filter(id => statusMap[id]?.videoStatus === 'done');
            return {
              ...prev,
              ...merged,
              isGenerating: false,
              isAutoGenerating: false,
              autoGeneratePhase: 'done',
              autoGenerateJobId: null,
              step: 4 as ExpressStep,
              maxStepReached: 4 as ExpressStep,
              assetsReady: true,
              selectedScenarioIds: successfulIds.length > 0 ? successfulIds : prev.selectedScenarioIds,
              mp4DownloadUrl: rd?.mp4_download_url || prev.mp4DownloadUrl,
              mp4Generating: false,
            };
          });
          setExpressJobParam(null);
          notifyVideosDone(jobId);
          const failed = rd?.videos_failed ?? 0;
          if (failed > 0) toast.error(`${failed}案の動画生成に失敗しました（成功分のみ出力します）`);
          else if (rd?.mp4_download_url) toast.success('動画生成が完了しました');
          else if (rd?.mp4_status === 'failed') toast.error('連結MP4の事前生成に失敗しました。動画タブから手動でエクスポートできます。');
        },
        onFailed: (message) => {
          setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null }));
          setExpressJobParam(null);
          toast.error(message);
        },
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '生成に失敗しました';
      setState(prev => ({ ...prev, isGenerating: false, isAutoGenerating: false, autoGeneratePhase: null, autoGenerateJobId: null }));
      setExpressJobParam(null);
      toast.error(message);
    }
  }, [state, effectiveTone, pollJob, toast, notifyVideosDone, logExpressToneEvent]);

  const goToStep = useCallback((step: ExpressStep) => {
    setState(prev => ({ ...prev, step, maxStepReached: Math.max(prev.maxStepReached, step) as ExpressStep }));
  }, []);

  // 動画 (Step 4) → 絵コンテ選択 (Step 3) へ戻る (絵コンテは生成済みのまま選び直せる)。
  const backToScenarioSelect = useCallback(() => {
    setState(prev => ({
      ...prev,
      step: 3 as ExpressStep,
      isAutoGenerating: false,
      autoGeneratePhase: 'storyboard',
      autoGenerateJobId: null,
      assetsReady: false,
      mp4Generating: false,
      isExporting: false,
      exportComplete: false,
    }));
  }, []);

  const reset = useCallback(() => {
    clearPendingExport();
    setExpressJobParam(null);
    setState(initialState);
  }, []);

  // URLパラメータからジョブIDを読み取り、ポーリングを復帰 (full / auto / scenario いずれも)
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    const jobId = getExpressJobParam();
    if (!jobId) return;
    resumedRef.current = true;

    // モーダルを開く
    setTimeout(() => window.dispatchEvent(new Event('openExpressMode')), 0);

    setState(prev => ({
      ...prev,
      step: 3 as ExpressStep,
      maxStepReached: 3 as ExpressStep,
      isGenerating: true,
      autoGenerateJobId: jobId,
    }));

    resumeJobPolling(jobId);
  }, [resumeJobPolling]);

  return {
    ...state,
    amazonAccounts: amazonAccounts || [],
    // 商品AIプロファイル推奨 (計画書§5.4)。StepSettings が「おすすめ」既定表示に使う。
    recommendedTone,
    effectiveTone,
    setAsin,
    setAccountId,
    setTone,
    setAspectRatio,
    setScenarioCount,
    setDuration,
    setSelectedPresetActors,
    searchAsin,
    registerProduct,
    selectManualProduct,
    startGeneration,
    startFullGeneration,
    selectScenario,
    startVideoGeneration,
    exportMp4Zip,
    exportMp4Concat,
    goToStep,
    backToScenarioSelect,
    reset,
  };
}
